import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface StatusFunnelItem {
  status: string;
  count: number;
  stuck: number;
}

interface StatusFunnelProps {
  items: StatusFunnelItem[];
  stuckThresholdHours: number;
}

// Human-friendly labels + ordering for the lifecycle. Anything not listed here
// falls through to the bottom as-is.
const ORDER = [
  'DRAFT',
  'UPLOADING',
  'UPLOADED',
  'FINALIZED',
  'LIVE',
  'UPLOADING_FAILED',
] as const;

const LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  UPLOADING: 'Uploading',
  UPLOADED: 'Uploaded',
  FINALIZED: 'Finalized',
  LIVE: 'Live',
  UPLOADING_FAILED: 'Upload failed',
};

export function StatusFunnel({
  items,
  stuckThresholdHours,
}: StatusFunnelProps): JSX.Element {
  const ordered = [...items].sort((a, b) => {
    const ai = ORDER.indexOf(a.status as (typeof ORDER)[number]);
    const bi = ORDER.indexOf(b.status as (typeof ORDER)[number]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  const max = Math.max(1, ...ordered.map((i) => i.count));

  return (
    <ul className="flex flex-col gap-3">
      {ordered.map((it) => {
        const pct = (it.count / max) * 100;
        const isLive = it.status === 'LIVE';
        const isFailure = it.status === 'UPLOADING_FAILED';
        const hasStuck = it.stuck > 0;
        return (
          <li key={it.status} className="flex items-center gap-3">
            <div className="flex w-28 shrink-0 items-center gap-1.5">
              {isLive ? (
                <CheckCircle2 size={14} className="text-brand-cyan" />
              ) : isFailure ? (
                <AlertTriangle size={14} className="text-state-danger" />
              ) : hasStuck ? (
                <AlertTriangle size={14} className="text-state-warning" />
              ) : (
                <span className="block h-2 w-2 rounded-full bg-fg-subtle" />
              )}
              <span className="text-sm font-medium">
                {LABELS[it.status] ?? it.status}
              </span>
            </div>
            <div className="flex-1 h-2 rounded-full bg-surface-divider/30 overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all',
                  isLive
                    ? 'bg-brand-cyan'
                    : isFailure
                      ? 'bg-state-danger'
                      : hasStuck
                        ? 'bg-state-warning'
                        : 'bg-fg-subtle/60',
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex items-baseline gap-2 w-24 justify-end">
              <span className="text-lg font-extrabold tabular-nums">
                {it.count.toLocaleString()}
              </span>
              {hasStuck && (
                <span
                  title={`${it.stuck} stuck > ${stuckThresholdHours}h`}
                  className="text-xs font-medium text-state-warning"
                >
                  {it.stuck} stuck
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
